// client/src/components/Goals/GoalCard.js
import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

/**
 * GoalCard — displays a single savings goal with progress.
 * Props:
 *   goal         — the goal object (with currentAmount, progressPercent)
 *   onEdit       — (goal) => void
 *   onDelete     — (goal) => void
 *   formatCurrency — (amount) => string  (optional, defaults to € formatting)
 */
const GoalCard = ({ goal, onEdit, onDelete, formatCurrency }) => {
  const fmt = formatCurrency || ((n) => `€ ${Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

  const targetDateLabel = goal.targetDate
    ? new Date(goal.targetDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : null;

  const daysRemaining = goal.targetDate
    ? Math.ceil((new Date(goal.targetDate) - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const progress = Math.min(100, Math.max(0, goal.progressPercent || 0));

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        opacity: goal.isCompleted ? 0.85 : 1,
        border: goal.isCompleted ? '2px solid #22c55e' : undefined,
      }}
    >
      {/* Colored header bar */}
      <Box
        sx={{
          height: 6,
          bgcolor: goal.color || '#22c55e',
          width: '100%',
        }}
      />

      <CardContent sx={{ pt: 1.5, pb: '12px !important' }}>
        {/* Icon + Name row */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h5" component="span" lineHeight={1}>
              {goal.icon || '🎯'}
            </Typography>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                {goal.name}
              </Typography>
              {targetDateLabel && (
                <Typography variant="caption" color="text.secondary">
                  by {targetDateLabel}
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            {goal.isCompleted && (
              <Tooltip title="Goal completed!">
                <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 22 }} />
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => onEdit && onEdit(goal)} aria-label="Edit goal">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => onDelete && onDelete(goal)} aria-label="Delete goal">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {/* Progress row */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
          {/* Circular progress */}
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={100}
              size={56}
              thickness={4}
              sx={{ color: 'grey.200', position: 'absolute', top: 0, left: 0 }}
            />
            <CircularProgress
              variant="determinate"
              value={progress}
              size={56}
              thickness={4}
              sx={{ color: goal.color || '#22c55e' }}
            />
            <Box
              sx={{
                top: 0, left: 0, bottom: 0, right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                {Math.round(progress)}%
              </Typography>
            </Box>
          </Box>

          {/* Amounts */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              {fmt(goal.currentAmount)} <Typography component="span" variant="caption" color="text.secondary">/ {fmt(goal.targetAmount)}</Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fmt(Math.max(0, (goal.targetAmount || 0) - (goal.currentAmount || 0)))} remaining
            </Typography>

            {daysRemaining !== null && !goal.isCompleted && (
              <Box sx={{ mt: 0.5 }}>
                {daysRemaining > 0 ? (
                  <Chip
                    label={`${daysRemaining}d left`}
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      bgcolor: daysRemaining <= 30 ? 'warning.light' : 'action.hover',
                      color: daysRemaining <= 30 ? 'warning.dark' : 'text.secondary',
                    }}
                  />
                ) : (
                  <Chip label="Past deadline" size="small" color="error" sx={{ fontSize: '0.7rem' }} />
                )}
              </Box>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default GoalCard;
